import { Box, Typography, IconButton, Menu, MenuItem, ListItemText, Chip } from "@mui/material";
import * as React from "react";
import { useSelector } from "react-redux";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IconWithCount from "../../shared/IconWithCount";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import useTutorialTrigger from "../../../hooks/useTutorialTrigger";
import { TUTORIAL_FEATURES } from "../../../config/tutorials.config";

const DashboardTotalCount = ({
    isLockedTotal,
    background,
    handleChange,
    filterTotal,
    cardFilter,
    onMobileClick,
    filterListData
}) => {
    const isMobile  = useIsMobileDevice();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedFilterItem, setSelectedFilterItem] = React.useState(null);
    // const [filterItemMenuAnchor, setFilterItemMenuAnchor] = React.useState(null);
    const [clickedMenuItemEl, setClickedMenuItemEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const { triggerTutorial, trackFeatureUsage } = useTutorialTrigger();    

    const handleLockToggle = React.useCallback(() => {        
        trackFeatureUsage(TUTORIAL_FEATURES.LOCK_PERCENTAGE).catch((error) => {
            console.error('Failed to track lock percentage usage:', error);
        });
        if (!isLockedTotal) {        
            triggerTutorial(TUTORIAL_FEATURES.LOCK_PERCENTAGE).catch((error) => {
                console.error('Failed to trigger lock percentage tutorial:', error);
            });
        }
        if (typeof handleChange === 'function') {
            handleChange();
        }
    }, [handleChange, triggerTutorial, trackFeatureUsage, isLockedTotal]);

    // Get headerFilterItems from Redux slice
    const { headerFilterItems } = useSelector((state) => state.common);

    const handleClick = (event) => {
        if (isMobile) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (item, event) => {
        
        // Open submenu for items with count > 0
        if (item.count > 0) {
            setSelectedFilterItem(item);
            // Use the clicked menu item element for submenu positioning
            setClickedMenuItemEl(event.currentTarget);
            // setFilterItemMenuAnchor(event.currentTarget);
        } else {
            // Only close the menu if we're not opening a submenu
            handleClose();
        }
        // if (onMobileClick) {
        //     onMobileClick(item);
        // }
    };

    const handleFilterItemMenuClose = () => {
        // setFilterItemMenuAnchor(null);
        setSelectedFilterItem(null);
        setClickedMenuItemEl(null);
    };

    // Generate filter menu items based on Redux slice data
    const getFilterMenuItems = () => {
        const items = [];

        // Use the same data structure as HeaderFilter from Redux
        const shouldShowDefault = headerFilterItems?.metadata?.shouldShowDefault || false;

        if (headerFilterItems?.items && Array.isArray(headerFilterItems.items)) {
            headerFilterItems.items.forEach((item, index) => {
                // Only show items that are active filters (have data)
                if ((item.isActiveFilter && item.filterCount > 0) || item.isMain) {
                    if(item.isMain && !item.isTransferTypeMatch) return;                    
                    // Convert HeaderFilter items to mobile menu format
                    const mobileItem = {
                        ...item,
                        label: item.label,
                        type: item.type || "label",
                        dataKey: item.dataKey, // This is the key we need for filterListData and cardFilter
                        isMain: item.isMain || false,
                        isDefault: item.isDefault || false,
                        isLocked: item.isLocked || false,
                        count: item.filterCount || 0
                    };

                    items.push(mobileItem);
                }
            });
        }
        if (shouldShowDefault) {            
            const percentageBy = headerFilterItems?.metadata?.percentageBy || "Default";
            const isLockedByCensus = headerFilterItems?.metadata?.lockedTotal && headerFilterItems?.metadata?.lockedTotalBy === 'census';
            
            items.unshift({
                label: percentageBy,
                type: "label",
                dataKey: "default",
                isMain: false,
                isDefault: true,
                isLocked: isLockedByCensus,
                count: 0
            });
        }
        // If no items from Redux, fallback to the original logic
        if (items.length === 0 && cardFilter) {
            const filterTypes = [
                { key: 'totalMain', label: 'Total Main', isMain: true },
                { key: 'doctorData', label: 'Doctor Data' },
                { key: 'insuranceData', label: 'Insurance Data' },
                { key: 'floorsData', label: 'Floors Data' },
                { key: 'ninetyDaysData', label: 'Ninety Days Data' }
            ];

            filterTypes.forEach(filterType => {
                const filterData = cardFilter[filterType.key];
                if (filterData && Array.isArray(filterData) && filterData.length > 0) {
                    items.push({
                        label: filterType.label,
                        dataKey: filterType.key,
                        isMain: filterType.isMain || false,
                        count: filterData.length,
                        isLocked: isLockedTotal,
                        data: [],
                        cardFilter: filterData
                    });
                }
            });

            Object.keys(cardFilter).forEach(key => {
                if (!['totalMain', 'doctorData', 'insuranceData', 'floorsData', 'ninetyDaysData', 'priorityData', 'mainPriorityData'].includes(key)) {
                    const filterData = cardFilter[key];
                    if (filterData && Array.isArray(filterData) && filterData.length > 0) {
                        items.push({
                            label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
                            dataKey: key,
                            isMain: false,
                            count: filterData.length,
                            isLocked: isLockedTotal,
                            data: [],
                            cardFilter: filterData
                        });
                    }
                }
            });
        }

        return items;
    };

    return (
        <>
            <Typography
                component={isMobile ? IconButton : 'div'}
                onClick={isMobile ? handleClick : undefined}
                sx={{
                    fontFamily: "inter",
                    textTransform: "uppercase",
                    color: "#fff",
                    display: "flex",
                    direction: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    ...(isMobile && {
                        height: "35px",
                        cursor: 'pointer',
                        '&:hover': {
                            opacity: 0.8
                        }
                    })
                }}
            >
                <Box component="div" sx={{ mb: "-12px" }}>
                    <Typography
                        noWrap
                        color="#fff"
                        sx={{
                            fontWeight: 500,
                            fontFamily: "inter",
                            fontSize: "14px",
                            ...(isMobile && {
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '4px 4px',
                                fontSize: '10px',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                }
                            })
                        }}
                    >
                        # percentage against:
                        {isMobile && (
                            <KeyboardArrowDownIcon
                                sx={{
                                    fontSize: '12px',
                                    color: '#fff',
                                    transition: 'transform 0.2s ease',
                                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                                }}
                            />
                        )}
                    </Typography>
                </Box>
                <Box component={"div"} sx={{
                    alignItems: "center",
                    ...filterTotal <= 0 && {
                        marginLeft: "10px",
                    }
                }}>
                    <IconWithCount
                        isLockedTotal={isLockedTotal}
                        count={filterTotal}
                        handleChange={handleLockToggle}
                        background={background}
                        cardFilter={cardFilter}
                        isMobile={isMobile}
                    />
                </Box>
            </Typography>

            {/* Mobile Filter Menu */}
            {isMobile && (
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiPaper-root': {
                            minWidth: '250px',
                            maxWidth: '300px',
                            maxHeight: '400px',
                            overflow: 'auto',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            borderRadius: '8px'
                        }
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            px: 2,
                            py: 1.5,
                            fontWeight: 'bold',
                            borderBottom: '1px solid #e0e0e0',
                            color: '#333',
                            fontSize: '14px'
                        }}
                    >
                        Filter Options
                    </Typography>
                    {getFilterMenuItems()?.map((item, index) => (
                        <MenuItem
                            dense
                            key={index}
                            onClick={(event) => handleMenuItemClick(item, event)}
                            sx={{
                                py: 1.5,
                                px: 2,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                },
                                '&:active': {
                                    backgroundColor: '#e3f2fd'
                                }
                            }}
                        >
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontSize: '14px',
                                    color: item.isLocked ? '#999' : '#333',
                                    fontWeight: item.isMain ? 600 : 400
                                }}
                            />
                            {item.count > 0 && (
                                <Chip
                                    label={item.count}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#e3f2fd',
                                        color: '#4879f5',
                                        fontSize: '10px',
                                        height: '20px',
                                        minWidth: '20px',
                                        pointerEvents: 'none' // Make chip non-clickable
                                    }}
                                />
                            )}
                        </MenuItem>
                    ))}
                    {getFilterMenuItems()?.length === 0 && (
                        <MenuItem disabled sx={{ py: 2, px: 2 }}>
                            <ListItemText
                                primary="No filters available"
                                primaryTypographyProps={{
                                    fontSize: '14px',
                                    color: '#999',
                                    fontStyle: 'italic'
                                }}
                            />
                        </MenuItem>
                    )}
                </Menu>
            )}

            {/* Filter Item Details Submenu */}
            {isMobile && (
                <Menu
                    anchorEl={clickedMenuItemEl}
                    open={Boolean(clickedMenuItemEl)}
                    onClose={handleFilterItemMenuClose}
                    anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'left',
                    }}
                    sx={{
                        '& .MuiPaper-root': {
                            minWidth: '200px',
                            maxWidth: '300px',
                            maxHeight: '300px',
                            overflow: 'auto'
                        }
                    }}
                >
                             
                    {(() => {
                        // Use the same logic as HeaderItem component
                        const subItems = (() => {
                            const itemData = filterListData?.[selectedFilterItem?.dataKey];
                            const itemCardFilter = cardFilter?.[selectedFilterItem?.dataKey];    
                            
                            if (!Array.isArray(itemData) || !Array.isArray(itemCardFilter)) {
                                return [];
                            }

                            return itemData
                                .filter(({ _id }) => itemCardFilter.includes(_id))
                                .map(({ label, total }) => ({ label, total }));
                        })();


                        if (subItems.length > 0) {
                            return subItems.map((item, index) => (
                                <MenuItem
                                    dense
                                    key={index}
                                    sx={{
                                        py: 0.5,
                                        px: 2,                                        
                                        '&:hover': {
                                            backgroundColor: '#bbdefb'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontSize: '12px',
                                            color: '#1976d2',
                                            fontWeight: 600
                                        }}
                                    />                                  
                                </MenuItem>
                            ));
                        } else {
                            return (
                                <MenuItem disabled>
                                    <ListItemText
                                        primary="No selected items"
                                        primaryTypographyProps={{
                                            fontSize: '12px',
                                            color: '#999',
                                            fontStyle: 'italic'
                                        }}
                                    />
                                </MenuItem>
                            );
                        }
                    })()}
                </Menu>
            )}
        </>
    );
};

export default DashboardTotalCount;
